import z from "zod";
import {
  ItemScopeEnum,
  ItemTypeEnum,
  type AdditionalImpressionForRemovableProsthesisItem,
  type AddingTeethOnExistingDentureItem,
  type AbutmentItem,
  type ArchScopedItem,
  type BridgeItem,
  type CrownItem,
  type CustomAbutmentItem,
  type CustomTrayItem,
  type ImplantCrownItem,
  type InlayCoreCharacteristics,
  type InlayCoreConfiguration,
  type InlayCoreItem,
  type Item,
  type LayItem,
  type OrthoModelItem,
  type RetentionWireItem,
  type SpaceMaintainerItem,
  type StandardAbutmentItem,
  type VeneerItem,
  type WaxBiteItem,
  type DentureRepairItem,
  type ImplantBridgeItem,
  type BridgePonticItem,
  type RetainerItem,
  type BleachingTrayItem,
  type NightGuardItem,
} from "./items.types";
import {
  materialCharacteristicsSchema,
  materialCharacteristicsWithoutShadeSchema,
} from "../material";
import { teethArchSchema, toothSchema } from "../teeth";
import type { CreateMode } from "../types";
import { WaxUpTypeEnum, type WaxUpTypeConfiguration } from "./waxUp.types";
import {
  customAbutmentCharacteristicsSchema,
  implantCharacteristicsSchema,
  implantedItemCharacteristicsSchema,
  standardAbutmentCharacteristicsSchema,
} from "../implants";

export const baseItemSchema = z.object({
  scope: z.literal(ItemScopeEnum.tooth).optional(),
  tooth: toothSchema,
  type: z.enum(Object.values(ItemTypeEnum)),
  materialCharacteristics: materialCharacteristicsSchema,
}) satisfies z.ZodType<CreateMode<Item>>;

export const bridgeItemSchema = z.object({
  ...baseItemSchema.shape,
  type: z.literal(ItemTypeEnum.bridge),
}) satisfies z.ZodType<CreateMode<BridgeItem>>;

export const crownItemSchema = z.object({
  ...baseItemSchema.shape,
  type: z.literal(ItemTypeEnum.crown),
}) satisfies z.ZodType<CreateMode<CrownItem>>;

export const addingTeethOnExistingDentureItemSchema = z.object({
  scope: z.literal(ItemScopeEnum.tooth).optional(),
  tooth: toothSchema,
  type: z.literal(ItemTypeEnum.addingTeethOnExistingDenture),
}) satisfies z.ZodType<CreateMode<AddingTeethOnExistingDentureItem>>;

export const layItemSchema = z.object({
  ...baseItemSchema.shape,
  type: z.literal(ItemTypeEnum.lay),
}) satisfies z.ZodType<CreateMode<LayItem>>;

export const veneerItemSchema = z.object({
  ...baseItemSchema.shape,
  type: z.literal(ItemTypeEnum.veneer),
}) satisfies z.ZodType<CreateMode<VeneerItem>>;

export const inlayCoreCharacteristicsSchema = z.object({
  hasClavette: z.boolean(),
  scanPost: z.string().min(1),
}) satisfies z.ZodType<InlayCoreCharacteristics>;

export const inlayCoreItemSchema = z.object({
  scope: z.literal(ItemScopeEnum.tooth).optional(),
  tooth: toothSchema,
  type: z.literal(ItemTypeEnum.inlayCore),
  materialCharacteristics: materialCharacteristicsWithoutShadeSchema,
  inlayCoreCharacteristics: inlayCoreCharacteristicsSchema,
}) satisfies z.ZodType<CreateMode<InlayCoreItem>>;

export const inlayCoreConfigurationSchema = z.object({
  materialCharacteristics: materialCharacteristicsWithoutShadeSchema,
  inlayCoreCharacteristics: inlayCoreCharacteristicsSchema,
}) satisfies z.ZodType<InlayCoreConfiguration>;

export const archItemSchema = z.object({
  scope: z.literal(ItemScopeEnum.arch),
  arch: teethArchSchema,
  type: z.enum(Object.values(ItemTypeEnum)),
}) satisfies z.ZodType<CreateMode<ArchScopedItem>>;

export const waxBiteItemSchema = z.object({
  ...archItemSchema.shape,
  type: z.literal(ItemTypeEnum.waxBite),
}) satisfies z.ZodType<CreateMode<WaxBiteItem>>;

export const retentionWireItemSchema = z.object({
  ...archItemSchema.shape,
  type: z.literal(ItemTypeEnum.retentionWire),
}) satisfies z.ZodType<CreateMode<RetentionWireItem>>;

export const customTrayItemSchema = z.object({
  ...archItemSchema.shape,
  type: z.literal(ItemTypeEnum.customTray),
}) satisfies z.ZodType<CreateMode<CustomTrayItem>>;

export const spaceMaintainerItemSchema = z.object({
  ...baseItemSchema.shape,
  tooth: toothSchema,
  type: z.literal(ItemTypeEnum.spaceMaintainer),
  materialCharacteristics: z.undefined(),
}) satisfies z.ZodType<CreateMode<SpaceMaintainerItem>>;

export const orthoModelItemSchema = z.object({
  ...archItemSchema.shape,
  type: z.literal(ItemTypeEnum.orthoModel),
}) satisfies z.ZodType<CreateMode<OrthoModelItem>>;

export const dentureRepairItemSchema = z.object({
  ...archItemSchema.shape,
  type: z.literal(ItemTypeEnum.dentureRepair),
}) satisfies z.ZodType<CreateMode<DentureRepairItem>>;

export const additionalImpressionForRemovableProsthesisItemSchema = z.object({
  ...archItemSchema.shape,
  type: z.literal(ItemTypeEnum.additionalImpressionForRemovableProsthesis),
}) satisfies z.ZodType<
  CreateMode<AdditionalImpressionForRemovableProsthesisItem>
>;

export const waxUpTypeConfigurationSchema = z.object({
  ...baseItemSchema.shape,
  tooth: toothSchema,
  type: z.literal(ItemTypeEnum.waxUp),
  materialCharacteristics: z.undefined().optional(),
  waxUpType: z.enum(WaxUpTypeEnum),
}) satisfies z.ZodType<CreateMode<WaxUpTypeConfiguration>>;

export const implantCrownItemSchema = z.object({
  ...baseItemSchema.shape,
  tooth: toothSchema,
  type: z.literal(ItemTypeEnum.implantCrown),
  implantedItemCharacteristics: implantedItemCharacteristicsSchema,
}) satisfies z.ZodType<CreateMode<ImplantCrownItem>>;

export const standardAbutmentItemSchema = z.object({
  ...baseItemSchema.shape,
  tooth: toothSchema,
  type: z.literal(ItemTypeEnum.abutment),
  abutmentCharacteristics: standardAbutmentCharacteristicsSchema,
  implantCharacteristics: implantCharacteristicsSchema,
  materialCharacteristics: z.undefined(),
}) satisfies z.ZodType<CreateMode<StandardAbutmentItem>>;

export const customAbutmentItemSchema = z.object({
  ...baseItemSchema.shape,
  tooth: toothSchema,
  type: z.literal(ItemTypeEnum.customAbutment),
  abutmentCharacteristics: customAbutmentCharacteristicsSchema,
  implantCharacteristics: implantCharacteristicsSchema,
  materialCharacteristics: z.undefined(),
}) satisfies z.ZodType<CreateMode<CustomAbutmentItem>>;

export const abutmentItemSchema = z.union([
  standardAbutmentItemSchema,
  customAbutmentItemSchema,
]) satisfies z.ZodType<CreateMode<AbutmentItem>>;

export const implantBridgeItemSchema = z.object({
  ...baseItemSchema.shape,
  tooth: toothSchema,
  type: z.literal(ItemTypeEnum.bridgeImplant),
  implantedItemCharacteristics: implantedItemCharacteristicsSchema,
}) satisfies z.ZodType<CreateMode<ImplantBridgeItem>>;

export const bridgePonticItemSchema = z.object({
  ...baseItemSchema.shape,
  tooth: toothSchema,
  type: z.literal(ItemTypeEnum.bridgePontic),
}) satisfies z.ZodType<CreateMode<BridgePonticItem>>;

export const nightGuardItemSchema = z.object({
  ...archItemSchema.shape,
  type: z.literal(ItemTypeEnum.nightGuard),
  materialCharacteristics: materialCharacteristicsWithoutShadeSchema,
}) satisfies z.ZodType<CreateMode<NightGuardItem>>;

export const bleachingTrayItemSchema = z.object({
  ...archItemSchema.shape,
  type: z.literal(ItemTypeEnum.bleachingTray),
}) satisfies z.ZodType<CreateMode<BleachingTrayItem>>;

export const retainerItemSchema = z.object({
  ...archItemSchema.shape,
  type: z.literal(ItemTypeEnum.retainer),
}) satisfies z.ZodType<CreateMode<RetainerItem>>;
